import { useEffect, useRef, useState } from "react";

const ACTION_MENU_INTERACTIVE_SELECTOR = ".resolutions-v2-action-menu, .resolutions-v2-action-trigger, .resolutions-v2-action-wrap";

export const isActionMenuEventTarget = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest(ACTION_MENU_INTERACTIVE_SELECTOR);

type UseActionMenuAutoCloseArgs = {
  isOpen: boolean;
  isHovered: boolean;
  closeMenu: () => void;
  timeoutMs?: number;
};

type UseFloatingActionMenuArgs = {
  openMenuId: string | null;
  setOpenMenuId: (next: string | null) => void;
  menuWidth?: number;
};

export const useActionMenuAutoClose = ({ isOpen, isHovered, closeMenu, timeoutMs = 3000 }: UseActionMenuAutoCloseArgs) => {
  const closeMenuRef = useRef(closeMenu);

  useEffect(() => {
    closeMenuRef.current = closeMenu;
  }, [closeMenu]);

  useEffect(() => {
    if (!isOpen || isHovered) return;
    const timeoutId = window.setTimeout(() => closeMenuRef.current(), timeoutMs);
    return () => window.clearTimeout(timeoutId);
  }, [isHovered, isOpen, timeoutMs]);
};

export const useFloatingActionMenu = ({ openMenuId, setOpenMenuId, menuWidth = 170 }: UseFloatingActionMenuArgs) => {
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!openMenuId) return;
    const closeMenu = () => setOpenMenuId(null);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openMenuId, setOpenMenuId]);

  useEffect(() => {
    if (!openMenuId) setActionMenuPosition(null);
  }, [openMenuId]);

  const openActionMenuAtTrigger = (menuId: string, trigger: HTMLElement, menuHeight = 96) => {
    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    const left = Math.min(Math.max(margin, rect.right - menuWidth), Math.max(margin, window.innerWidth - menuWidth - margin));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    setActionMenuPosition({ left, top });
    setOpenMenuId(menuId);
  };

  return { actionMenuPosition, openActionMenuAtTrigger };
};
